// Printable question paper for a test — the shared paper the teacher app opens
// from "View in PDF". Its own route outside the sidebar layout, so it prints as
// a clean document; the test detail opens it in a new tab, because that detail
// lives in the Tests page's state and a same-tab visit would lose it.
import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QuestionPaper, paperSubtitle, paperTariffs } from '@shared-ui/question-paper';
import { questionApi } from '../../api/questionApi';
import { questionSetApi } from '../../api/questionSetApi';

export function TestPdfView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: setData } = useQuery({
    queryKey: ['question-set', id],
    queryFn: () => questionSetApi.get(id!),
    enabled: !!id,
  });
  const qs = setData?.questionSet;
  const questionIds = qs?.questionIds;

  // Per id, as the teacher app does: the paper needs sub-parts and which option
  // is correct, and the batch question-list projection carries neither.
  const { data: questions, isLoading } = useQuery({
    queryKey: ['test-pdf-questions', questionIds],
    queryFn: async () => {
      const results = await Promise.all(
        questionIds!.map(qid => questionApi.getQuestionById(qid).catch(() => null)),
      );
      return results.filter(q => q !== null);
    },
    enabled: !!questionIds && questionIds.length > 0,
  });

  // Each question's marks in the paper's own terms, as the teacher's view
  // prints them — "10 marks — (i) 4 working · (ii) 4 working · option ±2".
  const tariffs = useMemo(
    () => (qs && questions ? paperTariffs(qs, questions) : undefined),
    [qs, questions],
  );

  return (
    <QuestionPaper
      tariffs={tariffs}
      title={qs?.title ?? 'Questions'}
      subtitle={qs ? paperSubtitle(qs) : undefined}
      sections={qs?.sections}
      questions={questions}
      loading={isLoading || !qs}
      onBack={() => {
        // Opened in a tab of its own: closing it returns to the detail behind it.
        if (window.history.length > 1) navigate(-1);
        else { window.close(); navigate('/tests'); }
      }}
    />
  );
}
